import { Component, computed, inject, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject, switchMap, takeUntil, of } from 'rxjs';
import { ApproveVendorContext } from '../../models/assign-vendor.model';
import { AssignVendorService } from '../../services/assign-vendor.service';
import { VendorBillsService } from '../../services/vendor-bills.service';

/**
 * The "Additional Approval" step V1 offers right after an estimate is approved: it emails the vendor the approval
 * (subject "Additional Approval for <PO>") and moves the job to Pending Return ETA. Admin V2's Assign Vendor tab has it;
 * the chat pop-up's Approve did not, so an on-site estimate approved there reached the vendor with no email.
 *
 * Same server calls as the Assign Vendor tab ("approve-vendor-context" then "send-additional-approval"), so the email and
 * the status change are exactly V1's. The pop-up opens it with {@link open} once Approve has succeeded. Nothing here is
 * needed to approve: closing it leaves the approval as it is.
 */
const APPROVAL_HEADING = '<b>Approval To Proceed : </b>';

/** The work order's saved text is HTML and often already starts with the "Approval To Proceed" heading; show it as plain lines. */
export function toPlainApprovalText(html: string | null | undefined): string {
  return (html ?? '')
    .replace(/<b>\s*Approval To Proceed\s*:\s*<\/b>/gi, '')
    .replace(/<br\s*\/?>|<\/p>|<\/div>|<\/h\d>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** What the email carries: the bold heading, then the admin's text with its line breaks kept. */
export function toApprovalHtml(text: string): string {
  const body = text
    .trim()
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\r?\n/g, '<br/>');
  return `${APPROVAL_HEADING}${body}`;
}

@Component({
  selector: 'app-onsite-additional-approval-modal',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './onsite-additional-approval-modal.component.html',
  styleUrl: './onsite-additional-approval-modal.component.scss',
})
export class OnsiteAdditionalApprovalModalComponent {
  private readonly vendorBills = inject(VendorBillsService);
  private readonly assignVendor = inject(AssignVendorService);

  /** The email went out (or the admin closed the step); the pop-up reloads its thread. */
  readonly finished = output<{ sent: boolean }>();

  readonly visible = signal(false);
  readonly loading = signal(false);
  readonly sending = signal(false);
  readonly error = signal('');

  readonly vendorName = signal('');
  readonly ctx = signal<ApproveVendorContext | null>(null);
  readonly approvalText = signal('');
  readonly selectedContacts = signal<string[]>([]);
  readonly selectedEstimate = signal<string | null>(null);

  private jobVendorKey = '';
  private readonly cancel$ = new Subject<void>();

  readonly canSend = computed(
    () => !this.sending() && this.selectedContacts().length > 0,
  );

  /**
   * Loads the vendor's approval context and shows the step. When the vendor has no work order (V1 shows nothing then
   * either) nothing opens and `finished` fires with sent=false, so the caller just carries on.
   */
  open(jobKey: string, vendorKey: string, estimateKey: string | null): void {
    this.cancel$.next();
    this.error.set('');
    this.loading.set(true);
    this.visible.set(true);

    this.vendorBills
      .getCreateEstimateModal(jobKey)
      .pipe(
        switchMap((res) => {
          const jv = (res?.data?.vendors ?? []).find(
            (v) => v.vendorKey?.toLowerCase() === vendorKey.toLowerCase(),
          );
          if (!jv) return of(null);
          this.jobVendorKey = jv.jobVendorKey;
          this.vendorName.set(jv.vendorName ?? '');
          return this.assignVendor.getApproveVendorContext(jv.jobVendorKey);
        }),
        takeUntil(this.cancel$),
      )
      .subscribe((res) => {
        this.loading.set(false);
        const data = res?.data;
        if (!res?.status || !data || !data.hasWorkOrder || data.revVendorDne === -11) {
          this.visible.set(false);
          this.finished.emit({ sent: false });
          return;
        }
        this.ctx.set(data);
        this.approvalText.set(toPlainApprovalText(data.description));
        const defaults = data.contacts.filter((c) => c.isDefault).map((c) => c.contactKey);
        this.selectedContacts.set(defaults);
        this.selectedEstimate.set(
          (estimateKey && data.estimates.some((e) => e.estimateKey === estimateKey)
            ? estimateKey
            : data.estimates[0]?.estimateKey) ?? null,
        );
      });
  }

  toggleContact(contactKey: string, checked: boolean): void {
    const set = new Set(this.selectedContacts());
    if (checked) set.add(contactKey);
    else set.delete(contactKey);
    this.selectedContacts.set([...set]);
  }

  send(): void {
    const data = this.ctx();
    if (!data || !data.workOrderKey || !this.canSend()) return;
    this.sending.set(true);
    this.error.set('');

    this.assignVendor
      .sendAdditionalApproval({
        jobVendorKey: this.jobVendorKey,
        vendorContactKeys: this.selectedContacts(),
        revVendorDne: data.revVendorDne ?? 0,
        vendorDne: data.vendorDne ?? 0,
        approvalText: toApprovalHtml(this.approvalText()),
        workOrderKey: data.workOrderKey,
        estimateKey: this.selectedEstimate(),
        onsiteApproval: true,
      })
      .pipe(takeUntil(this.cancel$))
      .subscribe((res) => {
        this.sending.set(false);
        if (!res?.status) {
          this.error.set(res?.message || 'The approval email could not be sent.');
          return;
        }
        this.visible.set(false);
        this.finished.emit({ sent: true });
      });
  }

  close(): void {
    if (this.sending()) return;
    this.cancel$.next();
    this.visible.set(false);
    this.loading.set(false);
    this.finished.emit({ sent: false });
  }
}
